/**
 * RehearsalNotesPage ✦ — iter204
 * ─────────────────────────────────────────────────────────────
 * Operator-only "tiny pad" for capturing physical, emotional,
 * environmental observations from real rehearsals. The bridge
 * from real rooms → future iterations.
 *
 * Restraint contract (iter204 brief):
 *   • Very small. Very calm.
 *   • Operator-only. /debug/ route, no auth gate — consistent
 *     with the existing debug surfaces.
 *   • Append-only. No edit. No delete. No tags. No analytics.
 *   • One textarea. One optional free-text context line. One
 *     capture gesture. A quiet reverse-chronological timeline.
 *   • OPERATOR eyebrow so the atmosphere tests recognise it as
 *     operator-only.
 *
 * What we are deliberately NOT building:
 *   • upload metrics
 *   • tag taxonomies
 *   • search / filter UI
 *   • CSV export
 *   • dashboards
 *   • engagement scoring
 * The brief: "institutional memory", not analytics.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK = '#1f1409';
const INK_SOFT = 'rgba(31,20,9,0.72)';
const INK_FAINT = 'rgba(31,20,9,0.55)';
const PARCHMENT = '#f5efdf';
const IVORY = '#fbf6ec';
const GOLD = '#a4824a';
const RULE = 'rgba(31,20,9,0.14)';
const FONT_DISPLAY = "'Cormorant Garamond','Playfair Display',Georgia,serif";
const FONT_BODY = "'Inter',system-ui,sans-serif";


// Format an ISO timestamp into the editorial register the rest of
// the operator surfaces use. We deliberately keep this terse — no
// "5 minutes ago" relative chatter; the rehearsal log is for later
// reading, not live-feed scanning.
const formatNoteTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit',
    });
    return `${date} · ${time}`;
  } catch {
    return '';
  }
};


const NoteRow = ({ note }) => (
  <article
    data-testid="rehearsal-note-row"
    style={{
      padding: '20px 0',
      borderBottom: `1px solid ${RULE}`,
    }}
  >
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'baseline', marginBottom: 8, gap: 12,
    }}>
      <span
        data-testid="rehearsal-note-time"
        style={{
          fontFamily: FONT_BODY, fontSize: 11,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: INK_FAINT,
        }}
      >
        {formatNoteTime(note.created_at)}
      </span>
      {note.context && (
        <span
          data-testid="rehearsal-note-context"
          style={{
            fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 13, color: GOLD,
          }}
        >
          {note.context}
        </span>
      )}
    </div>
    <p
      data-testid="rehearsal-note-body"
      style={{
        margin: 0, fontFamily: FONT_DISPLAY,
        fontSize: 18, lineHeight: 1.55, color: INK,
        whiteSpace: 'pre-wrap',
      }}
    >
      {note.note}
    </p>
  </article>
);


const RehearsalNotesPage = () => {
  const { slug } = useParams();
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [contextText, setContextText] = useState('');
  const [status, setStatus] = useState('idle'); // idle | capturing | settled | gathering
  const [errorLine, setErrorLine] = useState('');

  const refresh = useCallback(async () => {
    if (!slug) return;
    try {
      const r = await axios.get(`${API}/debug/rehearsal-notes/${slug}`);
      setNotes(r.data?.notes || []);
    } catch (e) {
      // Quiet — empty timeline is a fine atmospheric default.
      setNotes([]);
    }
  }, [slug]);

  useEffect(() => { refresh(); }, [refresh]);

  const capture = useCallback(async (e) => {
    if (e) e.preventDefault();
    const trimmed = noteText.trim();
    if (!trimmed) return;
    setStatus('capturing');
    setErrorLine('');
    try {
      await axios.post(`${API}/debug/rehearsal-notes/${slug}`, {
        note: trimmed,
        context: contextText.trim() || null,
      });
      setNoteText('');
      setContextText('');
      setStatus('settled');
      await refresh();
    } catch (err) {
      setStatus('gathering');
      setErrorLine(
        err?.response?.data?.detail || 'The pad is still gathering itself ✦'
      );
    }
  }, [slug, noteText, contextText, refresh]);

  return (
    <div
      data-testid="rehearsal-notes-page"
      data-status={status}
      style={{
        minHeight: '100vh', background: PARCHMENT, color: INK,
        fontFamily: FONT_BODY, padding: '24px 22px 80px',
      }}
    >
      <BackButton
        fallbackHref={`/event/${slug}`}
        label="Back"
        variant="on-light"
        testId="rehearsal-notes-back-btn"
      />

      <header style={{ maxWidth: 640, margin: '40px auto 0' }}>
        <p
          data-testid="rehearsal-notes-eyebrow"
          style={{
            margin: 0, fontFamily: FONT_BODY, fontSize: 11,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: INK_FAINT,
          }}
        >
          REHEARSAL NOTES ✦  ·  OPERATOR
        </p>
        <h1
          data-testid="rehearsal-notes-title"
          style={{
            margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontWeight: 500,
            fontSize: 'clamp(34px,5vw,48px)', lineHeight: 1.05,
            letterSpacing: '-0.015em', color: INK,
          }}
        >
          What the room taught ✦
        </h1>
        <p style={{
          margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 16, color: INK_SOFT, maxWidth: 520, lineHeight: 1.5,
        }}>
          A quiet pad for what you saw, heard, and felt in the room. Append-only.
        </p>
      </header>

      <form
        data-testid="rehearsal-notes-form"
        onSubmit={capture}
        style={{
          maxWidth: 640, margin: '32px auto 0',
          background: IVORY, padding: '24px 24px 20px',
          border: `1px solid ${RULE}`,
        }}
      >
        <label
          htmlFor="rehearsal-notes-textarea"
          style={{
            display: 'block', fontFamily: FONT_BODY, fontSize: 11,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: INK_FAINT, marginBottom: 8,
          }}
        >
          The observation
        </label>
        <textarea
          id="rehearsal-notes-textarea"
          data-testid="rehearsal-notes-textarea"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder=""
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box',
            background: 'transparent', border: `1px solid ${RULE}`,
            padding: '12px 14px', fontFamily: FONT_DISPLAY, fontSize: 17,
            lineHeight: 1.5, color: INK, resize: 'vertical',
            outline: 'none',
          }}
        />

        <label
          htmlFor="rehearsal-notes-context"
          style={{
            display: 'block', marginTop: 14, fontFamily: FONT_BODY, fontSize: 11,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: INK_FAINT, marginBottom: 8,
          }}
        >
          Where (optional)
        </label>
        <input
          id="rehearsal-notes-context"
          data-testid="rehearsal-notes-context-input"
          type="text"
          value={contextText}
          onChange={(e) => setContextText(e.target.value)}
          maxLength={80}
          placeholder=""
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box',
            background: 'transparent', border: `1px solid ${RULE}`,
            padding: '10px 12px', fontFamily: FONT_DISPLAY, fontSize: 15,
            color: INK, outline: 'none',
          }}
        />

        <div style={{
          marginTop: 18, display: 'flex',
          justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
        }}>
          {errorLine ? (
            <span
              data-testid="rehearsal-notes-status"
              style={{
                fontFamily: FONT_DISPLAY, fontStyle: 'italic',
                fontSize: 14, color: GOLD,
              }}
            >
              {errorLine}
            </span>
          ) : <span />}
          <button
            type="submit"
            data-testid="rehearsal-notes-capture-btn"
            disabled={!noteText.trim() || status === 'capturing'}
            style={{
              padding: '10px 22px', borderRadius: 4,
              border: `1px solid ${GOLD}`,
              background: noteText.trim() ? 'transparent' : 'rgba(164,130,74,0.06)',
              color: INK, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 15, letterSpacing: '0.02em',
              cursor: noteText.trim() && status !== 'capturing' ? 'pointer' : 'default',
            }}
          >
            Capture ✦
          </button>
        </div>
      </form>

      <section
        data-testid="rehearsal-notes-timeline"
        style={{ maxWidth: 640, margin: '40px auto 0' }}
      >
        {notes.length === 0 ? (
          <p
            data-testid="rehearsal-notes-empty-line"
            style={{
              margin: 0, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 16, color: INK_FAINT, textAlign: 'center',
            }}
          >
            The pad is waiting for the room ✦
          </p>
        ) : (
          notes.map((n) => <NoteRow key={n.note_id} note={n} />)
        )}
      </section>
    </div>
  );
};

export default RehearsalNotesPage;
